import { NavLink, Link, useNavigate } from "react-router-dom";
import { Home, Newspaper, BookOpen, Star, Wine, Gift, Users, CalendarDays, Shield, Search, Bell, Moon, Sun, LogOut, User, Menu } from "lucide-react";
import { Wordmark, Logo } from "@/components/kit";
import { useTheme } from "@/context/ThemeContext";
import { useAuth } from "@/context/AuthContext";
import { useState } from "react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel } from "@/components/ui/dropdown-menu";

const NAV = [
  { to: "/", icon: Home, label: "Home", end: true },
  { to: "/feed", icon: Newspaper, label: "Feed" },
  { to: "/recipes", icon: BookOpen, label: "Recipes" },
  { to: "/ratings", icon: Star, label: "Ratings" },
  { to: "/build-bar", icon: Wine, label: "Build My Bar" },
  { to: "/registry", icon: Gift, label: "Registry" },
  { to: "/groups", icon: Users, label: "Groups" },
  { to: "/planner", icon: CalendarDays, label: "Planner" },
];
const MOBILE_NAV = [NAV[0], NAV[1], NAV[4], NAV[5], NAV[3]];

function Sidebar() {
  const { user } = useAuth();
  const items = user?.role === "admin" ? [...NAV, { to: "/admin", icon: Shield, label: "Admin" }] : NAV;
  return (
    <aside className="sticky top-0 hidden h-screen w-60 shrink-0 flex-col border-r border-border bg-card/40 px-4 py-6 lg:flex">
      <Link to="/" className="mb-8 px-2"><Wordmark /></Link>
      <nav className="flex flex-1 flex-col gap-1">
        {items.map((n) => (
          <NavLink key={n.to} to={n.to} end={n.end} data-testid={`nav-${n.label.toLowerCase().replace(/ /g, "-")}`}
            className={({ isActive }) => `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${isActive ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-muted hover:text-foreground"}`}>
            <n.icon size={18} /> {n.label}
          </NavLink>
        ))}
      </nav>
      <div className="mt-4 rounded-xl border border-border bg-gradient-to-br from-primary/10 to-secondary/10 p-4">
        <p className="font-display text-lg leading-tight">The most original gift registry.</p>
        <Link to="/registry" className="mt-2 inline-block text-xs font-semibold text-primary hover-underline">Create a Registry →</Link>
      </div>
    </aside>
  );
}

function Header() {
  const { theme, toggle } = useTheme();
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [q, setQ] = useState("");
  return (
    <header className="sticky top-0 z-40 glass border-b border-border">
      <div className="flex items-center gap-3 px-4 py-3 lg:px-6">
        <Link to="/" className="lg:hidden"><Logo /></Link>
        <form onSubmit={(e) => { e.preventDefault(); if (q) nav(`/ratings?q=${q}`); }} className="relative flex-1 max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input data-testid="global-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search bottles, recipes, groups…"
            className="w-full rounded-full border border-border bg-background/60 py-2 pl-9 pr-4 text-sm outline-none focus:border-primary" />
        </form>
        <div className="ml-auto flex items-center gap-1.5">
          <button data-testid="theme-toggle" onClick={toggle} className="rounded-full p-2 text-muted-foreground hover:bg-muted hover:text-foreground" aria-label="toggle theme">
            {theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}
          </button>
          <button data-testid="notifications-btn" className="relative rounded-full p-2 text-muted-foreground hover:bg-muted hover:text-foreground">
            <Bell size={18} /><span className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-primary" />
          </button>
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger data-testid="profile-menu-trigger" className="ml-1 flex items-center gap-2 rounded-full border border-border p-0.5 pr-3 hover:border-primary/40">
                <img src={user.avatar} alt="" className="h-8 w-8 rounded-full object-cover" />
                <span className="hidden text-sm font-medium sm:block">{user.name?.split(" ")[0]}</span>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <DropdownMenuLabel>{user.name}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => nav("/profile")} data-testid="menu-profile"><User size={15} className="mr-2" /> My Profile</DropdownMenuItem>
                {user.role === "admin" && <DropdownMenuItem onClick={() => nav("/admin")}><Shield size={15} className="mr-2" /> Admin</DropdownMenuItem>}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => { logout(); nav("/"); }} data-testid="logout-btn"><LogOut size={15} className="mr-2" /> Log out</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <button data-testid="header-login-btn" onClick={() => nav("/login")} className="ml-1 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground">Sign in</button>
          )}
        </div>
      </div>
    </header>
  );
}

function BottomNav() {
  return (
    <nav className="fixed bottom-0 left-0 right-0 z-40 glass border-t border-border lg:hidden">
      <div className="flex items-center justify-around px-2 py-2">
        {MOBILE_NAV.map((n) => (
          <NavLink key={n.to} to={n.to} end={n.end} data-testid={`mobilenav-${n.label.toLowerCase().replace(/ /g, "-")}`}
            className={({ isActive }) => `flex flex-col items-center gap-0.5 rounded-lg px-3 py-1 text-[10px] font-medium ${isActive ? "text-primary" : "text-muted-foreground"}`}>
            <n.icon size={20} /> {n.label.split(" ")[0]}
          </NavLink>
        ))}
      </div>
    </nav>
  );
}

export default function Layout({ children }) {
  return (
    <div className="flex min-h-screen bg-background">
      <Sidebar />
      <div className="flex min-w-0 flex-1 flex-col">
        <Header />
        <main className="flex-1 pb-24 lg:pb-8">{children}</main>
      </div>
      <BottomNav />
    </div>
  );
}
